import Vue from 'vue';
import Router from 'vue-router';

// common
import NotFoundComponent from '@/components/common/NotFoundComponent';
import LoadingComponent from '@/components/common/Loading';

// guards
import AuthGuard from './auth-guard';
import LoginGuard from './login-guard';
import RoleGuard from './role-guard';

const AsyncComponentOpts = {
  // A component to use while the async component is loading
  loading: LoadingComponent,
  // A component to use if the load fails
  error: NotFoundComponent,
  // Delay before showing the loading component. Default: 200ms.
  delay: 200
  // The error component will be displayed if a timeout is
  // provided and exceeded. Default: Infinity.
  // timeout: 3000
};

const Profile = () =>
  Object.assign({}, AsyncComponentOpts, {
    component: import('@/components/common/Profile')
  });

// login
const Login = () =>
  Object.assign({}, AsyncComponentOpts, {
    component: import('@/components/login/Login')
  });
const LoginForm = () =>
  Object.assign({}, AsyncComponentOpts, {
    component: import('@/components/login/LoginForm')
  });
const ForgotPassword = () =>
  Object.assign({}, AsyncComponentOpts, {
    component: import('@/components/login/ForgotPassword')
  });

// max-admin dashboard
const MaxAdmin = () =>
  Object.assign({}, AsyncComponentOpts, {
    component: import('@/components/max-admin/MaxAdmin')
  });
const MaxAdminComponents = {
  Dashboard: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/dashboard/Dashboard')
    }),
  Tenants: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/tenants/Tenants')
    }),
  TenantsComponents: {
    TenantsTable: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/tenants/TenantsTable')
      }),
    NewTenant: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/tenants/NewTenant')
      }),
    Tenant: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/tenants/Tenant')
      })
  },
  Fnb: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/fnb/Fnb')
    }),
  FnbComponents: {
    Vendors: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/fnb/Vendors')
      }),
    NewVendor: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/fnb/NewVendor')
      }),
    Vendor: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/fnb/Vendor')
      })
  },
  VisitorLog: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/vms/visitor-log/VisitorLog')
    }),
  VisitorLogComponents: {
    VisitorLogWidgets: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/visitor-log/VisitorLogWidgets')
      }),
    VisitorLogDetails: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/visitor-log/VisitorLogDetails')
      })
  },
  Invites: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/vms/invites/Invites')
    }),
  InviteComponents: {
    InvitesTable: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/invites/InvitesTable')
      }),
    NewInvite: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/invites/NewInvite')
      }),
    Invite: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/invites/Invite')
      }),
    BulkInvite: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/invites/BulkInvite')
      })
  },
  Visitors: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/vms/visitors/Visitors')
    }),
  VisitorComponents: {
    VisitorsTable: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/visitors/VisitorsTable')
      }),
    Visitor: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/visitors/Visitor')
      })
  },
  Facilities: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/facilities/Facilities')
    }),
  Amenities: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/facilities/Amenities')
    }),
  Devices: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/vms/devices/Devices')
    }),

  HelpDesk: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/help-desk/HelpDesk')
    }),

  Analytics: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/analytics/Analytics')
    }),

  AnalyticsComponents: {
    HelpDesk: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/analytics/HelpDesk')
      }),
    Facilities: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/analytics/Facilities')
      }),
    ActiveUsers: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/analytics/Users')
      })
  },

  SettingsComponents: {
    MaxTowers: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/MaxTowers')
      }),
    Account: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/vms/account/Account')
      }),
    InvitesSettings: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/vms/invites-settings/InvitesSettings')
      }),
    AppSettings: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/vms/app-settings/MiscSettings')
      }),
    CheckinFlow: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/vms/checkin-flow/CheckinFlow')
      }),
    CheckinFlowChildren: {
      VisitorTypes: () =>
        Object.assign({}, AsyncComponentOpts, {
          component: import('@/components/max-admin/settings/vms/checkin-flow/VisitorTypes')
        }),
      VisitorType: () =>
        Object.assign({}, AsyncComponentOpts, {
          component: import('@/components/max-admin/settings/vms/checkin-flow/VisitorType')
        })
    },
    Notificaions: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/vms/notification-settings/NotificationSettings')
      }),
    HelpDesk: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/HelpDesk')
      })
  }
};

// admin
const Admin = () =>
  Object.assign({}, AsyncComponentOpts, {
    component: import('@/components/admin/Admin')
  });
const AdminComponents = {
  Dashboard: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/admin/dashboard/Dashboard')
    }),
  Employees: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/admin/employees/Employees')
    }),
  EmployeesComponents: {
    EmployeesTable: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/admin/employees/EmployeesTable')
      }),
    NewEmployee: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/admin/employees/NewEmployee')
      }),
    Employee: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/admin/employees/Employee')
      }),
    ImportEmployees: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/admin/employees/ImportEmployees')
      })
  },
  VisitorLog: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/vms/visitor-log/VisitorLog')
    }),
  VisitorLogComponents: {
    VisitorLogWidgets: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/visitor-log/VisitorLogWidgets')
      }),
    VisitorLogDetails: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/visitor-log/VisitorLogDetails')
      })
  },
  Invites: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/vms/invites/Invites')
    }),
  InviteComponents: {
    InvitesTable: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/invites/InvitesTable')
      }),
    Invite: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/vms/invites/Invite')
      })
  },
  Facilities: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/facilities/Facilities')
    }),
  Amenities: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/facilities/Amenities')
    }),
  HelpDesk: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/max-admin/help-desk/HelpDesk')
    }),

  Invoices: () =>
    Object.assign({}, AsyncComponentOpts, {
      component: import('@/components/admin/invoices/Invoices')
    }),

  SettingsComponents: {
    Company: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/admin/settings/Company')
      }),
    HelpDesk: () =>
      Object.assign({}, AsyncComponentOpts, {
        component: import('@/components/max-admin/settings/HelpDesk')
      })
  }
};

Vue.use(Router);

export default new Router({
  mode: 'hash',
  routes: [
    {
      path: '/',
      component: LoadingComponent,
      beforeEnter: AuthGuard
    },
    {
      path: '/login',
      component: Login,
      beforeEnter: LoginGuard,
      children: [
        {
          path: '/',
          name: 'LoginForm',
          component: LoginForm
        },
        {
          path: 'forgot-password',
          name: 'ForgotPassword',
          component: ForgotPassword
        },
        {
          path: '*',
          component: NotFoundComponent
        }
      ]
    },
    {
      path: '/max-admin',
      component: MaxAdmin,
      beforeEnter: RoleGuard,
      children: [
        {
          path: '/',
          redirect: 'dashboard'
        },
        {
          path: 'profile',
          name: 'MaxAdmin.Profile',
          component: Profile
        },
        {
          path: 'dashboard',
          name: 'MaxAdmin.Dashboard',
          component: MaxAdminComponents.Dashboard
        },
        {
          path: 'tenants',
          component: MaxAdminComponents.Tenants,
          children: [
            {
              path: '',
              name: 'MaxAdmin.Tenants',
              component: MaxAdminComponents.TenantsComponents.TenantsTable
            },
            {
              path: 'add',
              name: 'MaxAdmin.Tenants.Add',
              component: MaxAdminComponents.TenantsComponents.NewTenant
            },
            {
              path: ':tenantId',
              component: MaxAdminComponents.TenantsComponents.Tenant,
              props: true
            }
          ]
        },
        {
          path: 'vms/visitor-log',
          component: MaxAdminComponents.VisitorLog,
          children: [
            {
              path: '/',
              name: MaxAdmin.VisitorLogWidgets,
              component: MaxAdminComponents.VisitorLogComponents.VisitorLogWidgets
            },
            {
              path: ':vlid',
              component: MaxAdminComponents.VisitorLogComponents.VisitorLogDetails,
              props: true
            }
          ]
        },
        {
          path: 'vms/invites',
          component: MaxAdminComponents.Invites,
          children: [
            {
              path: '/',
              name: MaxAdmin.InvitesTable,
              component: MaxAdminComponents.InviteComponents.InvitesTable
            },
            {
              path: 'add',
              name: MaxAdmin.NewInvite,
              component: MaxAdminComponents.InviteComponents.NewInvite
            },
            {
              path: 'bulk-invite',
              name: MaxAdmin.BulkInvite,
              component: MaxAdminComponents.InviteComponents.BulkInvite
            },
            {
              path: ':inviteid',
              component: MaxAdminComponents.InviteComponents.Invite,
              props: true
            }
          ]
        },
        {
          path: 'vms/visitors',
          component: MaxAdminComponents.Visitors,
          children: [
            {
              path: '/',
              name: 'MaxAdmin.Visitors',
              component: MaxAdminComponents.VisitorComponents.VisitorsTable
            },
            {
              path: ':vid',
              component: MaxAdminComponents.VisitorComponents.Visitor,
              props: true
            }
          ]
        },
        {
          path: 'vms/devices',
          component: MaxAdminComponents.Devices,
          name: MaxAdmin.Devices
        },
        {
          path: 'facilities',
          component: MaxAdminComponents.Facilities,
          name: MaxAdmin.Facilities
        },
        {
          path: 'amenities',
          component: MaxAdminComponents.Amenities,
          name: MaxAdmin.Amenities
        },
        {
          path: 'fnb',
          component: MaxAdminComponents.Fnb,
          children: [
            {
              path: '/',
              redirect: 'vendors'
            },
            {
              path: 'vendors',
              name: 'MaxAdmin.Fnb.Vendors',
              component: MaxAdminComponents.FnbComponents.Vendors
            },
            {
              path: 'add-vendor',
              name: 'MaxAdmin.Fnb.AddVendor',
              component: MaxAdminComponents.FnbComponents.NewVendor
            },
            {
              path: 'vendors/:vendorId',
              component: MaxAdminComponents.FnbComponents.Vendor,
              props: true
            }
          ]
        },
        {
          path: 'help-desk',
          component: MaxAdminComponents.HelpDesk,
          name: 'MaxAdmin.HelpDesk'
        },
        {
          path: 'analytics',
          component: MaxAdminComponents.Analytics,
          children: [
            {
              path: '/',
              redirect: 'helpDesk'
            },
            {
              path: 'helpDesk',
              name: 'MaxAdmin.Analytics.HelpDesk',
              component: MaxAdminComponents.AnalyticsComponents.HelpDesk
            },
            {
              path: 'facilities',
              name: 'MaxAdmin.Analytics.Facilities',
              component: MaxAdminComponents.AnalyticsComponents.Facilities
            },
            {
              path: 'users',
              name: 'MaxAdmin.Analytics.ActiveUsers',
              component: MaxAdminComponents.AnalyticsComponents.ActiveUsers
            }
          ]
        },
        {
          path: 'settings',
          redirect: 'settings/max-towers'
        },
        {
          path: 'settings/max-towers',
          component: MaxAdminComponents.SettingsComponents.MaxTowers
        },
        {
          path: 'settings/help-desk',
          component: MaxAdminComponents.SettingsComponents.HelpDesk
        },
        {
          path: 'settings/vms/app-settings',
          component: MaxAdminComponents.SettingsComponents.AppSettings,
          name: 'MaxAdmin.AppSettings'
        },
        {
          path: 'settings/vms/account',
          component: MaxAdminComponents.SettingsComponents.Account,
          name: 'MaxAdmin.Account'
        },
        {
          path: 'settings/vms/invites-settings',
          component: MaxAdminComponents.SettingsComponents.InvitesSettings,
          name: 'MaxAdmin.InvitesSettings'
        },
        {
          path: 'settings/vms/checkinflow',
          component: MaxAdminComponents.SettingsComponents.CheckinFlow,
          children: [
            {
              path: '',
              name: 'LocationAdmin.CheckinFlow',
              component: MaxAdminComponents.SettingsComponents.CheckinFlowChildren.VisitorTypes
            },
            {
              path: ':vtid',
              component: MaxAdminComponents.SettingsComponents.CheckinFlowChildren.VisitorType,
              props: true
            }
          ]
        },
        {
          path: 'settings/vms/notifications',
          component: MaxAdminComponents.SettingsComponents.Notificaions,
          name: 'MaxAdmin.Notifications'
        },
        {
          path: '*',
          component: NotFoundComponent
        }
      ]
    },
    {
      path: '/admin',
      component: Admin,
      beforeEnter: RoleGuard,
      children: [
        {
          path: '/',
          redirect: 'dashboard'
        },
        {
          path: 'profile',
          name: 'Admin.Profile',
          component: Profile
        },
        {
          path: 'dashboard',
          name: 'Admin.Dashboard',
          component: AdminComponents.Dashboard
        },
        {
          path: 'employees',
          component: AdminComponents.Employees,
          children: [
            {
              path: '',
              name: 'Admin.Employees',
              component: AdminComponents.EmployeesComponents.EmployeesTable
            },
            {
              path: 'add',
              name: 'Admin.Employees.Add',
              component: AdminComponents.EmployeesComponents.NewEmployee
            },
            {
              path: 'import',
              name: 'Admin.Employees.Import',
              component: AdminComponents.EmployeesComponents.ImportEmployees
            },
            {
              path: ':eid',
              component: AdminComponents.EmployeesComponents.Employee,
              props: true
            }
          ]
        },
        {
          path: 'vms/visitor-log',
          component: AdminComponents.VisitorLog,
          children: [
            {
              path: '/',
              name: MaxAdmin.VisitorLogWidgets,
              component: AdminComponents.VisitorLogComponents.VisitorLogWidgets
            },
            {
              path: ':vlid',
              component: AdminComponents.VisitorLogComponents.VisitorLogDetails,
              props: true
            }
          ]
        },
        {
          path: 'vms/invites',
          component: AdminComponents.Invites,
          children: [
            {
              path: '/',
              name: MaxAdmin.InvitesTable,
              component: AdminComponents.InviteComponents.InvitesTable
            },
            {
              path: ':inviteid',
              component: AdminComponents.InviteComponents.Invite,
              props: true
            }
          ]
        },
        {
          path: 'facilities',
          component: AdminComponents.Facilities,
          name: Admin.Facilities
        },
        {
          path: 'amenities',
          component: AdminComponents.Amenities,
          name: Admin.Amenities
        },
        {
          path: 'help-desk',
          component: AdminComponents.HelpDesk,
          name: 'Admin.HelpDesk'
        },
        {
          path: 'analytics',
          component: MaxAdminComponents.Analytics,
          children: [
            {
              path: '/',
              redirect: 'helpDesk'
            },
            {
              path: 'helpDesk',
              name: 'Admin.Analytics.HelpDesk',
              component: MaxAdminComponents.AnalyticsComponents.HelpDesk
            },
            {
              path: 'facilities',
              name: 'Admin.Analytics.Facilities',
              component: MaxAdminComponents.AnalyticsComponents.Facilities
            }
          ]
        },
        {
          path: 'invoices',
          component: AdminComponents.Invoices,
          name: 'Admin.Invoices'
        },
        {
          path: 'settings',
          redirect: 'settings/company'
        },
        {
          path: 'settings/company',
          component: AdminComponents.SettingsComponents.Company
        },
        {
          path: 'settings/help-desk',
          component: MaxAdminComponents.SettingsComponents.HelpDesk
        },
        {
          path: '*',
          component: NotFoundComponent
        }
      ]
    },
    {
      path: '*',
      name: '404',
      component: NotFoundComponent
    }
  ]
});
